import { ComponentType, Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { SerializedStyles, css } from '@emotion/react';
import { Page } from '@frontend/page';
import { useMerchants } from '@frontend/payments-hooks';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { BreadcrumbPathProps, ContentLoader } from '@frontend/design-system';
import { MerchantAlertBanner } from '../merchant-status/alert-banner';
import { MerchantError } from '../MerchantError';
import { PaymentError } from '../PaymentError';
import { StripeSignupPage } from '../stripe-signup-page/stripe-signup-page';

interface CommonProps {
  title?: React.ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  css?: SerializedStyles | SerializedStyles[];
  error?: Error | null;
  showLocationChip?: boolean;
  showLocationCount?: boolean;
  breadcrumbs?: BreadcrumbPathProps[];
  className?: string;
}
interface PaymentsPagePropsWithoutPrint extends CommonProps {
  printDialogue?: undefined;
  showPrint?: undefined;
  setShowPrint?: undefined;
}
interface PaymentsPagePropsWithPrint extends CommonProps {
  printDialogue: ComponentType<React.PropsWithChildren<{ onClose: () => void }>>;
  showPrint: boolean;
  setShowPrint: Dispatch<SetStateAction<boolean>>;
}
type PaymentsPageProps = PaymentsPagePropsWithoutPrint | PaymentsPagePropsWithPrint;

const styles = {
  pageTitleSection: css`
    & .title {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: ${theme.spacing(2, 2)};
    }
  `,
};

export const PaymentsPage: FC<React.PropsWithChildren<PaymentsPageProps>> = ({
  title,
  subtitle,
  action,
  children,
  printDialogue,
  showPrint,
  setShowPrint,
  error,
  showLocationChip = false,
  showLocationCount = false,
  breadcrumbs,
  ...rest
}) => {
  const PrintDialogue = printDialogue;
  const { selectedLocationIds } = useAppScopeStore();
  const { hasMerchantsError, allMerchantsLoaded, anyLocationHasPayments } = useMerchants(selectedLocationIds);
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  if (!allMerchantsLoaded) {
    // still loading
    return <ContentLoader show={true} />;
  }

  if (error) {
    // massive error, show "something went wrong"
    return <PaymentError />;
  }

  if (!hasMerchantsError && !anyLocationHasPayments) {
    // no error, but also no payments: show new stripe signup page
    return <StripeSignupPage locationIds={selectedLocationIds} />;
  }

  // will show merchants error below
  // will show payments below
  return (
    <Page css={styles.pageTitleSection} maxWidth={'100%'} {...rest}>
      <Page.Header>
        {!isMobile && <Page.Header.Breadcrumbs breadcrumbs={breadcrumbs} />}
        <Page.Header.Heading
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
          `}
        >
          <Page.Header.Title title={title}>
            <Page.Header.LocationChip showLocationChip={showLocationChip} showLocationCount={showLocationCount} />
          </Page.Header.Title>

          {!hasMerchantsError && anyLocationHasPayments && subtitle && <Page.Header.Subtitle subtitle={subtitle} />}

          {action && <Page.Header.Action>{action}</Page.Header.Action>}
        </Page.Header.Heading>
      </Page.Header>

      <Page.Body>
        {hasMerchantsError ? (
          <MerchantError />
        ) : (
          <>
            <MerchantAlertBanner />
            {children}
            {showPrint && PrintDialogue && <PrintDialogue onClose={() => setShowPrint?.(false)} />}
          </>
        )}
      </Page.Body>
    </Page>
  );
};
